import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { setUser, setToken } from '../../redux/auth/authSlice';
import {
  completeMagicLinkSignIn,
  isMagicLinkCallback,
} from '../../services/firebase/firebaseService';
import { User } from '../../types';
import { getPostAuthDestination } from '../../utils/authDestination';
import Image from '../../components/common/Image';
import {
  TaskyAuthLayout,
  TaskyButton,
  TaskyFormGroup,
  TaskyInput,
  TaskySpinner,
} from '../../components/tasky';
import { trackMetaCompleteRegistration } from '../../utils/metaPixel';
import { trackXSignUp } from '../../utils/xPixel';
import {
  trackOnce,
  getAnonymousId,
  ANALYTICS_EVENTS,
} from '../../utils/productAnalytics';

const authLogo = (
  <Image category="common" fileName="logo.png" alt="Tasky" width={100} />
);

type PageState = 'pending' | 'need_email' | 'error';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CompleteSignInPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [state, setState] = useState<PageState>('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  const [isResubmitting, setIsResubmitting] = useState(false);
  const hasProcessedRef = useRef(false);

  const urlRef = useRef('');

  const runCompleteSignIn = async (emailOverride?: string) => {
    try {
      const response = await completeMagicLinkSignIn(
        urlRef.current,
        emailOverride,
      );
      if (response?.token) dispatch(setToken(response.token));
      if (response?.user) dispatch(setUser(response.user as User));

      const orgId = response?.user?.organization_id;
      if (!orgId) {
        trackMetaCompleteRegistration(response?.user as User | undefined);
        trackXSignUp(response?.user as User | undefined);
        // BI: signup_completed（新規登録のみ。org 未確定 = まだ setup 前の新規ユーザー）。
        // token は completeMagicLinkSignIn 内で localStorage 保存済みのため track() の
        // Sanctum で user_id が確定する。properties.anonymous_id で anonymous_id ↔ user_id
        // をスティッチする。
        trackOnce(ANALYTICS_EVENTS.SIGNUP_COMPLETED, {
          anonymous_id: getAnonymousId(),
          method: 'magic_link',
        });
      }
      // 着地先の判定は LoginPage / SignupPage と共通
      // （utils/authDestination.ts）。
      navigate(getPostAuthDestination(response?.user as User | undefined));
      return true;
    } catch (err) {
      console.error('Magic link sign-in failed:', err);
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('メールアドレスが見つかりません')) {
        setState('need_email');
        setErrorMessage('');
      } else {
        setState('error');
        setErrorMessage(
          'サインインに失敗しました。リンクの有効期限が切れているか、一度使用済みの可能性があります。',
        );
      }
      return false;
    }
  };

  useEffect(() => {
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    const url = typeof window !== 'undefined' ? window.location.href : '';
    urlRef.current = url;

    if (!isMagicLinkCallback(url)) {
      setState('error');
      setErrorMessage(
        'リンクが無効です。メール内のログインリンクからもう一度アクセスしてください。',
      );
      return;
    }

    runCompleteSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = emailInput.trim();
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setEmailInputError('メールアドレスの形式が正しくありません');
      return;
    }
    setEmailInputError('');
    setIsResubmitting(true);
    await runCompleteSignIn(trimmed);
    setIsResubmitting(false);
  };

  if (state === 'pending') {
    return (
      <TaskyAuthLayout
        logo={authLogo}
        title="サインイン中..."
        description="リンクを検証しています。少々お待ちください。"
      >
        <div className="flex justify-center py-6">
          <TaskySpinner size="lg" />
        </div>
      </TaskyAuthLayout>
    );
  }

  if (state === 'need_email') {
    return (
      <TaskyAuthLayout
        logo={authLogo}
        title="メールアドレスを確認"
        description="別のタブや端末で開いたため、セキュリティ上もう一度メールアドレスを入力してください。"
      >
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <TaskyFormGroup
            label="サインインに使ったメールアドレス"
            required
            htmlFor="verify-email"
            error={emailInputError}
          >
            <TaskyInput
              id="verify-email"
              type="email"
              placeholder="you@company.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              hasError={Boolean(emailInputError)}
            />
          </TaskyFormGroup>

          <TaskyButton
            type="submit"
            isLoading={isResubmitting}
            className="w-full"
          >
            {isResubmitting ? '処理中...' : 'サインインする'}
          </TaskyButton>
        </form>
      </TaskyAuthLayout>
    );
  }

  return (
    <TaskyAuthLayout
      logo={authLogo}
      title="サインインできませんでした"
      description={errorMessage}
    >
      <TaskyButton
        type="button"
        onClick={() => navigate('/signup')}
        className="w-full"
      >
        サインアップに戻る
      </TaskyButton>
    </TaskyAuthLayout>
  );
};

export default CompleteSignInPage;
